import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/api.js";

// Query keys for companies
export const companyKeys = {
  all: ["companies"],
  lists: () => [...companyKeys.all, "list"],
  list: (filters) => [...companyKeys.lists(), filters],
  details: () => [...companyKeys.all, "detail"],
  detail: (id) => [...companyKeys.details(), id],
  documents: () => [...companyKeys.all, "documents"],
  documentsList: (id) => [...companyKeys.documents(), id],
  taxClearances: () => [...companyKeys.all, "taxClearances"],
  taxClearancesList: (id) => [...companyKeys.taxClearances(), id],
};

// Hook to detect company by SIREN
export const useDetectCompany = () => {
  return useMutation({
    mutationFn: (siren) => api.detectCompany(siren),
    onError: (error) => {
      console.error("Failed to detect company:", error);
    },
  });
};

// Hook to get companies list
export const useCompanies = (page = 1, limit = 10, search, riskLevel) => {
  return useQuery({
    queryKey: companyKeys.list({ page, limit, search, riskLevel }),
    queryFn: () => {
      if (search) {
        return api.searchCompanies(search, page, limit);
      }
      if (riskLevel) {
        return api.getCompaniesByRiskLevel(riskLevel, page, limit);
      }
      return api.getCompanies(page, limit);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to get a single company
export const useCompany = (id) => {
  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: () => api.getCompany(id),
    enabled: !!id, // Only run query if ID exists
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to create a new company
export const useCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyData) => api.createCompany(companyData),
    onSuccess: () => {
      // Invalidate and refetch companies list
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    },
    onError: (error) => {
      console.error("Failed to create company:", error);
    },
  });
};

// Hook to update a company
export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyData) => api.updateCompany(companyData),
    onSuccess: (updatedCompany) => {
      // Update the specific company in cache
      queryClient.setQueryData(
        companyKeys.detail(updatedCompany.id),
        updatedCompany,
      );
      // Invalidate lists to refresh them
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    },
    onError: (error) => {
      console.error("Failed to update company:", error);
    },
  });
};

// Hook to delete a company
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => api.deleteCompany(id),
    onSuccess: (_, deletedId) => {
      // Remove the deleted company from cache
      queryClient.removeQueries({ queryKey: companyKeys.detail(deletedId) });
      // Invalidate lists to refresh them
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    },
    onError: (error) => {
      console.error("Failed to delete company:", error);
    },
  });
};

// Hook to update company risk level
export const useUpdateCompanyRiskLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, riskLevel }) =>
      api.updateCompanyRiskLevel(id, riskLevel),
    onSuccess: (updatedCompany) => {
      // Update the specific company in cache
      queryClient.setQueryData(
        companyKeys.detail(updatedCompany.id),
        updatedCompany,
      );
      // Invalidate lists to refresh them
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    },
    onError: (error) => {
      console.error("Failed to update company risk level:", error);
    },
  });
};

// Hook to get company documents
export const useCompanyDocuments = (companyId) => {
  return useQuery({
    queryKey: companyKeys.documentsList(companyId),
    queryFn: () => api.getStatements(companyId),
    enabled: !!companyId, // Only run query if companyId exists
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Hook to get tax clearances
export const useTaxClearances = (companyId) => {
  return useQuery({
    queryKey: companyKeys.taxClearancesList(companyId),
    queryFn: () => api.getTaxDeclarations(companyId),
    enabled: !!companyId, // Only run query if companyId exists
    staleTime: 10 * 60 * 1000, // 10 minutes cache
  });
};
